import { useState } from 'react'
import { CalendarOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { resourceProfilesApi, type ResourceProfile } from '@/api/resourceCenterApi'
import { useUnavailabilityTypes } from '@/hooks/useResourceCenter'
import { todayIso } from '@/lib/resourceCalendar'
import { DateInput, ErrorLine, Field, Segmented, useProfileWrite } from './shared'

/**
 * "+ Add absence" (slide 13): picked from the center list, a date range,
 * whole days or a number of hours. Only the person's supervisor can approve
 * one, from the Time away tab.
 */
export function AbsenceDialog({
  profile,
  open,
  onOpenChange,
  initialDate,
}: {
  profile: ResourceProfile
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Pre-fill a single day, e.g. when opened from that day on the calendar. */
  initialDate?: string
}) {
  const types = useUnavailabilityTypes()
  const active = (types.data ?? []).filter(t => t.active)
  const [typeId, setTypeId] = useState('')
  const [from, setFrom] = useState(initialDate ?? todayIso())
  const [to, setTo] = useState(initialDate ?? todayIso())
  const [partial, setPartial] = useState<'day' | 'hours'>('day')
  const [hours, setHours] = useState('4')
  const [note, setNote] = useState('')
  const write = useProfileWrite()
  const type = active.find(t => t.id === typeId)

  const reset = () => {
    setTypeId('')
    setNote('')
  }
  const handleOpenChange = (o: boolean) => {
    onOpenChange(o)
    if (!o) reset()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2.5'>
            <span className='flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary'>
              <CalendarOff className='h-4 w-4' />
            </span>
            Plan an absence
          </DialogTitle>
          <DialogDescription>Taken off the shift before anything else, for {profile.name}.</DialogDescription>
        </DialogHeader>

        <div className='space-y-3'>
          <Field label='Type'>
            <Select
              value={typeId}
              onValueChange={v => {
                setTypeId(v)
                if (!active.find(t => t.id === v)?.allowPartial) setPartial('day')
              }}
            >
              <SelectTrigger className='h-9 text-sm' aria-label='Absence type'>
                <SelectValue placeholder={active.length ? 'Choose' : 'None on the center list'} />
              </SelectTrigger>
              <SelectContent>
                {active.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.code} · {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className='grid grid-cols-2 gap-2'>
            <Field label='From'>
              <DateInput
                value={from}
                onChange={v => {
                  setFrom(v)
                  if (to < v) setTo(v)
                }}
                ariaLabel='Absence from'
              />
            </Field>
            <Field label='To'>
              <DateInput value={to} onChange={setTo} min={from} ariaLabel='Absence to' />
            </Field>
          </div>
          <div className='flex items-center gap-2'>
            <Segmented
              ariaLabel='Full day or hours'
              value={partial}
              onChange={setPartial}
              disabled={!type?.allowPartial}
              options={[
                { value: 'day', label: 'Full day' },
                { value: 'hours', label: 'Hours' },
              ]}
            />
            {partial === 'hours' && (
              <Input
                type='number'
                min={0.25}
                max={24}
                step={0.25}
                value={hours}
                aria-label='Hours'
                onChange={e => setHours(e.target.value)}
                className='h-9 w-20 text-sm tabular-nums'
              />
            )}
          </div>
          <Field label='Note (optional)'>
            <Input value={note} onChange={e => setNote(e.target.value)} aria-label='Note' className='h-9 text-sm' />
          </Field>
          <ErrorLine message={write.error} />
        </div>

        <DialogFooter>
          <Button variant='ghost' onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!typeId || !from || !to || to < from || (partial === 'hours' && !(Number(hours) > 0)) || write.pending}
            onClick={() =>
              write.run(
                () =>
                  resourceProfilesApi.addUnavailability(profile.userId, {
                    unavailabilityTypeId: typeId,
                    fromDate: from,
                    toDate: to,
                    hours: partial === 'hours' ? Number(hours) : null,
                    note: note.trim() || undefined,
                  }),
                () => handleOpenChange(false),
              )
            }
          >
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
